import Vue from 'vue';
import Component from 'vue-class-component';
import {DownloadHelper} from '../../../../helpers/download';
import {Model, Prop, Watch} from 'vue-property-decorator';
import {DataManager, Visualisation, Toolbar, FilterBar, CustomDynamicView} from 'wc-visualisation';
import {Table, Icon, FieldType, Popover, pivotData} from 'wc-lib';
import {format} from 'date-fns';
import 'amcharts3';
import 'amcharts3/amcharts/serial';
import 'amstock3';
import 'amcharts3/amcharts/xy';
import 'amstock3/amcharts/amstock';

require('wc-lib/icons/electricity.svg');
require('wc-lib/icons/table.svg');
require('wc-lib/icons/download.svg');
require('wc-lib/icons/graph.svg');
require('wc-lib/icons/infocircle.svg');

const AmCharts = (window as any).AmCharts;

@Component({
  template: require('./supply-and-demand-breakdown.html'),
  name: 'supply-and-demand-breakdown',
  components: {
    'vis-visualisation': Visualisation,
    'vis-toolbar': Toolbar,
    'wc-icon': Icon,
    'vis-table': Table,
    'vis-filter-bar': FilterBar,
    'wc-popover': Popover
  }
})
export class SupplyAndDemandBreakdown extends Vue {
  amchart: any;
  dynamicView: CustomDynamicView<any>;
  data: Array<any> = [];
  availableRegions: string[] = [];
  selectedRegion: string = '';
  chartVisible = true;
  tableVisible = false;
  runDateTime: string = "";
  runNo: string = "";
  columns: Array<any> = [
    { title: 'Date', field: 'DAY', fieldType: FieldType.DateTime }, 
    { title: 'LOLP Operational Demand', field: 'WORST_INTERVAL_DEMAND' }, 
    { title: 'Installed Nameplate Capacity', field: 'AGGREGATEINSTALLEDCAPACITY'}, 
    { title: 'Availability of Scheduled Generation', field: 'PASAAVAILABILITY_SCHEDULED' }, 
    { title: 'Intermittent Generation', field: 'WORST_INTERVAL_INTGEN' }, 
    { title: 'Demand Side Participation', field: 'WORST_INTERVAL_DSP' }, 
    { title: 'Loss of Load Probability', field: 'LOSSOFLOADPROBABILITY' }, 
    { title: 'Loss of Load Magnitude', field: 'LOSSOFLOADMAGNITUDE' }
  ];
  infoText: string = `The Loss of Load Probability (LOLP) simulation assumes a worst-case scenario for demand net of intermittent generation in all regions simultaneously. As a result, the daily LOLP index provides an indication of the relative risk of supply interruptions, but is not a measure of the actual likelihood that there will be insufficient supply to meet demand on a given day. It is provided to highlight when supply adequacy is weakest and where generation and transmission maintenance should be avoided.

This visualisation features two charts over the same MTPASA horizon. The top chart shows a comparison between energy supply (availability of scheduled generation, intermittent generation and demand side participation) and operational demand (LOLP Operational Demand). 

The Installed Nameplate Capacity shows the aggregate nameplate capacity of all generation in the region that contributes to meeting operational demand. 
  
The Availability of Scheduled Generation shows the aggregate PASA availability of all scheduled generation for each day.
 
LOLP Operational Demand refers to the operational demand during either the period of maximum LOLP or the period of maximum operational demand net of intermittent generation if no unserved energy occurred during the day.

Intermittent Generation shows the average contribution from intermittent generation during the period described above.
  
Demand Side Participation shows the average contribution from demand side participation during the period described above.

Any interconnector support during the period described above is not shown on the chart, as interconnector flows are a variable dispatch outcome of the LOLP simulation rather than an input. The capability of the interconnector at times of LOLP Operational Demand is factored into the LOLP simulation in determining the daily LOLP index.
  
The lower figure shows the daily LOLP index across the same horizon. The colouring of the bars refers to the average magnitude of USE during periods with load shedding. Red indicates that the magnitude is high (greater than 400MW), orange that the magnitude is moderate (between 150 and 400 MW) and yellow that the magnitude is low (less than 150 MW).`;
  id: string;

  get chartButtonType(): string {
    return this.chartVisible ? 'primary' : 'ghost';
  }

  get tableButtonType(): string {
    return this.tableVisible ? 'primary' : 'ghost';
  }

  stockChartConfig: any = {
    type: 'stock',
    theme: 'simpleblue',
    dataSets: [{
      fieldMappings: [{
        fromField: 'WORST_INTERVAL_DEMAND',
        toField: 'WORST_INTERVAL_DEMAND'
      },{
        fromField: 'AGGREGATEINSTALLEDCAPACITY',
        toField: 'AGGREGATEINSTALLEDCAPACITY'
      },{
        fromField: 'PASAAVAILABILITY_SCHEDULED',
        toField: 'PASAAVAILABILITY_SCHEDULED'
      },{
        fromField: 'WORST_INTERVAL_INTGEN',
        toField: 'WORST_INTERVAL_INTGEN'
      },{
        fromField: 'WORST_INTERVAL_DSP',
        toField: 'WORST_INTERVAL_DSP'
      },{
        fromField: 'LOSSOFLOADPROBABILITY',
        toField: 'LOSSOFLOADPROBABILITY'
      }],
      categoryField: 'DAY'
    }],
    panels: [{
      showCategoryAxis: true,
      title: 'MW',
      percentHeight: 70,
      valueAxes: [{
        id: 'v1',
        stackType: 'regular'
      }],
      stockGraphs: [{
        id: 'g2',
        title: 'Installed Nameplate Capacity',
        valueField: 'AGGREGATEINSTALLEDCAPACITY',
        balloonText: '[[title]]: [[value]]',
        type: 'line',
        useDataSetColors: false,
        lineThickness: 2,
        stackable: false
      },{
        id: 'g3',
        title: 'Availability of Scheduled Generation',
        valueField: 'PASAAVAILABILITY_SCHEDULED',
        balloonText: '[[title]]: [[value]]',
        type: 'line',
        useDataSetColors: false,
        lineThickness: 0,
        fillAlphas: 0.6
      },{
        id: 'g4',
        title: 'Intermittent Generation',
        valueField: 'WORST_INTERVAL_INTGEN',
        balloonText: '[[title]]: [[value]]',
        type: 'line',
        useDataSetColors: false,
        lineThickness: 0,
        fillAlphas: 0.6
      },{
        id: 'g5',
        title: 'Demand Side Participation',
        valueField: 'WORST_INTERVAL_DSP',
        balloonText: '[[title]]: [[value]]',
        type: 'line',
        useDataSetColors: false,
        lineThickness: 0,
        fillAlphas: 0.6
      },{
        id: 'g1',
        title: 'LOLP Operational Demand',
        valueField: 'WORST_INTERVAL_DEMAND',
        balloonText: '[[title]]: [[value]]',
        type: 'line',
        useDataSetColors: false,
        lineColor: '#000000',
        lineThickness: 2,
        stackable: false
      }],
      stockLegend: {
        valueTextRegular: ' '
      }
    },{
      title: 'Daily LOLP index (%) - extreme weather conditions',
      percentHeight: 30,
      stockGraphs: [{
        valueField: 'LOSSOFLOADPROBABILITY',
        colorField: 'LolmColor',
        lineColorField: 'LolmColor',
        useDataSetColors: false,
        type: 'column',
        cornerRadiusTop: 2,
        fillAlphas: 1
      }],
      stockLegend: {
        valueTextRegular: ' ',
        markerType: 'none'
      }
    }],
    categoryAxesSettings: {
      equalSpacing: true,
      minPeriod: 'dd',
      maxSeries: '1000'
    },
    chartScrollbarSettings: {
      graph: 'g1'
    },
    chartCursorSettings: {
      valueBalloonsEnabled: true
    },
    panelSettings: {
      usePrefixes: true
    }
  };

  processData(data: any): any {
    for(let row of data) {
      switch (row.LOSSOFLOADMAGNITUDE.toUpperCase()) {
        case "LOW":
          row.LolmColor = '#f1f442';
          break;
        case "MEDIUM":
          row.LolmColor = '#f4a941';
          break;
        case "HIGH":
          row.LolmColor = '#f24126';
          break;
        default:
          row.LolmColor = '#050370';
          break;
      }
    }

    return data;
  }

  applyFilter() {
    this.dynamicView.removeFilters();
    this.dynamicView.applyFind({ 'REGIONID': this.selectedRegion });
    let data = this.dynamicView.data();
    this.data = this.processData(data);
  }

  async created() {
    this.id = await DataManager.addEndPoint('../../api/report/MTPASA_CHART_4', undefined, undefined, 'MTPASA_CHART_4', undefined, 'yyyy-MM-ddTHH:mm:ss', ['RUN_DATETIME', 'DAY']);
    this.dynamicView = DataManager.getDynamicView(this.id, 'supplyanddemand');
    this.dynamicView.removeFilters();
    let allData = this.dynamicView.data();
    this.runDateTime = format(allData[0].RUN_DATETIME, 'yyyy-MM-dd HH:mm:ss');
    this.runNo = allData[0].RUN_NO;
    allData.forEach( x => {
      if(this.availableRegions.indexOf(x.REGIONID) === -1) {
        this.availableRegions.push(x.REGIONID);
      }
      this.selectedRegion = x.REGIONID;
    });

    this.applyFilter();
  }

  mounted() {
    var imagesContext = require.context('!!file-loader?name=amcharts/images/[name].[ext]!amstock3/amcharts/images', true, /\.(png|jpg|gif|svg)$/);
    imagesContext.keys().forEach(imagesContext);
    this.amchart = AmCharts.makeChart(this.$refs.chartElement, this.stockChartConfig);
    if (this.data) this.onDataChange(this.data, null);
  }

  chartClick() {
    this.chartVisible = true;
    this.tableVisible = false;
  }

  tableClick() {
    this.tableVisible = true;
    this.chartVisible = false;
  }

  downloadClick() {
    DownloadHelper.download('mtpasa_chart_4', this.data, this.columns);
  }

  @Watch('data')
  onDataChange(newVal: any, oldVal: any) {
    this.amchart.dataSets[0].dataProvider = newVal;
    this.amchart.validateData();
  }

  @Watch('selectedRegion')
  onSelectedRegionChange(newValue: string, oldValue: string) {
    this.applyFilter();
  }
}



// WEBPACK FOOTER //
// src/views/electricity/nem/mtpasa/supply-and-demand-breakdown.ts